import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ConfirmationModal, ReactPipelineModal } from 'components';
import {
	BannerStatusState,
	SignDocModalState,
	SignedDocsState,
	UploadedTemplateDocsState,
	templateNameState,
	useSignedDocs,
} from 'global-stores';
import { useFreePlan, useNetwork, useNotification } from 'hooks';
import { ROUTES } from 'routes';
import { SimpliciSignIframe } from 'shared-components';
import { Kebab, PageHead, Pager, SearchBox } from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import {
	CreateTemplateSteps,
	EditTemplateName,
	SimpliciUseTemplate,
} from 'views/signed-doc/components';
import {
	IsCreateTemplateStepperOpen,
	SelectedSignerState,
	useSignedDoc,
	useSimpliciSign,
} from 'views/signed-doc/store';
import { provider as PROVIDER_LABEL } from 'views/signed-doc/constant';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './esign-templates-deck.scss';

const PAGE_SIZE = 15;

const labelSize = (size: string | number) => {
	if (typeof size === 'string' && /mb/i.test(size)) {
		return `${parseFloat(size.replace(/mb/gi, '').trim() || '0').toFixed(2)} MB`;
	}
	const n = parseFloat(String(size ?? 0));
	if (n > 1048576) return `${(n / 1048576).toFixed(2)} MB`;
	return `${(n / 1024).toFixed(2)} KB`;
};

const sizeOf = (item: any) => {
	let docSize = 0;
	(item.documents ?? []).forEach((d: any) => (docSize += Number(d.size ?? 0)));
	return labelSize(item.docRefId?.size ?? docSize);
};

/**
 * Genuinely different entity from an envelope (a reusable template, not a
 * signed packet) — a dense metadata table, not the .docbox thumbnail grid
 * (no image field on a template). Rename/delete reuse the legacy
 * EditTemplateName / ConfirmationModal + delete hook rather than re-deriving
 * them. The multi-step Create Template modal's provider-embed internals
 * (BoldSign/DocuSign iframe config) are NOT ported here — out of scope, same
 * call Kevin's design spec made; "+ New Template" opens the real modal via
 * its shared IsCreateTemplateStepperOpen atom and saves via the esign
 * provider path only. Picking DocuSign in that modal will not complete —
 * a known, flagged gap, not a silent failure to fix later.
 */
export const EsignTemplatesDeck = () => {
	const [signedDocs] = useRecoilState(SignedDocsState);
	const isVerified = useRecoilValue(BannerStatusState);
	const [addedFiles, setAddedFiles] = useRecoilState(UploadedTemplateDocsState);
	const [selectedSigner, setSelectedSigner] = useRecoilState(SelectedSignerState);
	const setSignDocState = useSetRecoilState(SignDocModalState);
	const [, setTempName] = useRecoilState(templateNameState);
	const setIsCreateOpen = useSetRecoilState(IsCreateTemplateStepperOpen);

	const { getDocuments, deleteDocument, isLoading, isLoaded } = useSignedDocs();
	const { errorNotification, successNotification, warningNotification } = useNotification();
	const { invitationSubscriptionCheck } = useFreePlan();
	const { checkUserWritePermission } = useUserRoles();
	const { post: saveTemplate } = useNetwork();
	const navigate = useNavigate();

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Templates),
		[checkUserWritePermission]
	);

	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);
	const [deleteId, setDeleteId] = useState('');
	const [confirmOpen, setConfirmOpen] = useState(false);

	/* THE CONFIGURE STEP. `/templates` does not finish a template — it answers
	   with a `prepareUrl` into the esign editor, and the template only becomes
	   real when the user saves in there. This deck used to toast "Document
	   Configured Successfully." the instant the POST returned, so every method
	   claimed success without the editor ever opening. */
	const [configDoc, setConfigDoc] = useState({ prepareUrl: '', documentId: '' });
	const [templateId, setTemplateId] = useState('');
	/* Whether the open editor is a template this deck just CREATED.
	   `handleCloseConfig` deletes an abandoned template, which is right for one
	   that was never configured and catastrophic for one being re-opened: Edit
	   patches an existing template back to an editable status, so an unsaved
	   close would have read as "abandoned" and destroyed a template the user
	   already had. */
	const [isNewTemplate, setIsNewTemplate] = useState(false);
	const { getTemplateStatus, deleteEsignTemplate } = useSignedDoc();
	const { updateTemplate } = useSimpliciSign();

	useEffect(() => {
		if (!signedDocs.length) getDocuments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filtered = useMemo(() => {
		const q = typed.trim().toLowerCase();
		if (!q) return signedDocs;
		return signedDocs.filter((item: any) => {
			const name = (item.name ?? item.documentName ?? '').toLowerCase();
			return name.includes(q);
		});
	}, [signedDocs, typed]);

	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const handleCreateNew = useCallback(() => {
		if (isVerified === false) {
			errorNotification('Please verify your email to create new template.');
			return;
		}
		if (invitationSubscriptionCheck && !checkUserWritePermission(PERMISSION_SCREEN.Billing)) {
			warningNotification('You need billing permission.');
			navigate(ROUTES.BILLING);
			return;
		}
		setIsCreateOpen(true);
		setSignDocState(false);
		setTempName('');
		setAddedFiles([]);
		setSelectedSigner('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isVerified,
		invitationSubscriptionCheck,
		checkUserWritePermission,
		navigate,
		setIsCreateOpen,
		setSignDocState,
		setTempName,
		setAddedFiles,
		setSelectedSigner,
	]);

	const handleSaveCreateDoc = useCallback(async () => {
		/* OVERLAY CARRIES NO DOCUMENT IDS, AND SAYING SO IS THE WHOLE POINT.
		   An overlay template is created empty and the document is supplied
		   inside the editor, so the server distinguishes it by `type: 'overlay'`
		   alone. This branch was missing: overlay posted `{documentIds: []}`,
		   which the server reads as an ordinary esign template with no
		   documents. Combined with the missing configure step below, picking
		   `esign Overlay` reported "Document Configured Successfully." without
		   ever opening anything — `SimpliciSignOverlay` calls this handler from
		   a mount effect, so there is no upload screen in between to make the
		   omission visible. */
		const isOverlay = selectedSigner === 'esign_overlay';
		const eSignPayload = { documentIds: addedFiles.map((f: any) => f._id) };
		const payload = isOverlay
			? { type: 'overlay' }
			: selectedSigner === 'eSign_editable'
			? { ...eSignPayload, type: 'editable' }
			: eSignPayload;

		const response: any = await saveTemplate('/templates?type=esign', payload);
		const {
			documents,
			prepareUrl,
			templateId: createdTemplateId,
			documentId,
		} = response ?? {};

		if (!prepareUrl) {
			errorNotification('Failed to open file. try again later');
			setIsCreateOpen(false);
			setSelectedSigner('');
			return;
		}

		setConfigDoc({ documentId: documents?.[0]?._id ?? documentId, prepareUrl });
		setTemplateId(createdTemplateId);
		setIsNewTemplate(true);
		setIsCreateOpen(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addedFiles, selectedSigner, saveTemplate, errorNotification, setIsCreateOpen, setSelectedSigner]);

	/** The editor reported a save — only now is the template real. */
	const handleConfiguredTemplate = useCallback(() => {
		setConfigDoc({ prepareUrl: '', documentId: '' });
		setTemplateId('');
		setIsNewTemplate(false);
		setSelectedSigner('');
		getDocuments();
		successNotification('Document Configured Successfully.');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getDocuments, successNotification, setSelectedSigner]);

	/* Closing the editor without saving leaves a template row the user never
	   finished. The legacy page removed it, and so does this: a `created` or
	   `decline` status means nothing was configured, and leaving those behind
	   fills the list with empty templates that cannot be opened. */
	const handleCloseConfig = useCallback(async () => {
		const abandoned = templateId;
		const wasNew = isNewTemplate;
		setConfigDoc({ prepareUrl: '', documentId: '' });
		setTemplateId('');
		setIsNewTemplate(false);
		setSelectedSigner('');
		/* Only a template this deck created in this sitting is disposable.
		   Editing re-opens an EXISTING one and leaves it in the same `created`
		   status the check below reads, so without this guard closing the editor
		   without saving would delete a template the user already had. */
		if (!abandoned || !wasNew) return;
		const status = await getTemplateStatus(abandoned);
		if (status === 'created' || status === 'decline') {
			deleteEsignTemplate(abandoned);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateId, isNewTemplate, getTemplateStatus, deleteEsignTemplate, setSelectedSigner]);

	const isCreateDisabled = useMemo(() => !addedFiles.length, [addedFiles.length]);

	/**
	 * Open the template's source document(s).
	 *
	 * THIS USED TO READ `item.docRefId?.url`, A FIELD A TEMPLATE DOES NOT HAVE.
	 * The optional chain meant the whole expression short-circuited to
	 * `undefined` and `&&` swallowed it — so the menu item was wired, enabled,
	 * and did nothing at all, with no error anywhere to say why.
	 *
	 * The resolution below is the legacy row action's own
	 * (`agreement-table-action.tsx`): `fileUrl` if the row carries one,
	 * otherwise every document's `path`. And when there is genuinely nothing to
	 * open it SAYS so, because a menu item that silently no-ops is the bug this
	 * is fixing, not a state worth reproducing.
	 */
	/**
	 * Re-open an existing template in the esign editor.
	 *
	 * The deck could only ever open the editor for a template it had just
	 * CREATED, so a saved template was a dead end: `Open file` downloads the raw
	 * PDF and there was nothing else on the row. The legacy page had this as its
	 * `Use template` action (`edit-template.tsx`) and the redesign dropped it.
	 *
	 * Two steps, both required. The row already carries its own `prepareUrl`, so
	 * no fetch is needed to find one — but a configured template is not editable
	 * until it is patched back (`PATCH /templates/:id?copy=false` with
	 * `source: 'default'`, which is exactly what `updateTemplate(id, false)`
	 * sends). Opening the editor without that patch loads a template the server
	 * will not accept edits to.
	 */
	const editTemplate = useCallback(
		async (item: any) => {
			const prepareUrl = item?.prepareUrl;
			const id = item?.templateId;
			if (!prepareUrl || !id) {
				errorNotification('This template cannot be edited.');
				return;
			}
			const ready = await updateTemplate(id, false);
			if (!ready) {
				errorNotification('Failed to open template. Try again later.');
				return;
			}
			setTemplateId(id);
			setIsNewTemplate(false);
			setConfigDoc({
				documentId: item?.documents?.[0]?._id ?? item?.documentId ?? '',
				prepareUrl,
			});
		},
		[updateTemplate, errorNotification]
	);

	const openTemplateFile = useCallback(
		(item: any) => {
			const direct = item?.fileUrl;
			if (direct) {
				window.open(direct, '_blank', 'noopener,noreferrer');
				return;
			}
			const paths = (item?.documents ?? [])
				.map((doc: any) => doc?.path)
				.filter(Boolean);
			if (!paths.length) {
				warningNotification('This template has no file to open.');
				return;
			}
			paths.forEach((path: string) =>
				window.open(path, '_blank', 'noopener,noreferrer')
			);
		},
		[warningNotification]
	);

	const askDelete = useCallback((id: string) => {
		setDeleteId(id);
		setConfirmOpen(true);
	}, []);

	const confirmDelete = useCallback(
		(isOpen: boolean, ok: any) => {
			setConfirmOpen(isOpen);
			if (ok && deleteId) {
				const index = signedDocs.findIndex((d: any) => d._id === deleteId);
				deleteDocument(deleteId, index);
			}
		},
		[deleteId, deleteDocument, signedDocs]
	);

	return (
		<div className="sv2-scope sv2 esign-templates-deck">
			<PageHead
				title="Templates"
				sub={<>{signedDocs.length} reusable signing templates</>}
				action={
					isUserPermissionWrite ? (
						<button className="cta" type="button" onClick={handleCreateNew}>
							+ New Template
						</button>
					) : undefined
				}
			/>

			<div className="ctl">
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					placeholder="Search templates"
					title="Searches template name"
				/>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl ttbl">
						<thead>
							<tr>
								<th className="subcol">Template Name</th>
								<th>Size</th>
								<th>Signers</th>
								<th>Provider</th>
								<th>Type</th>
								<th>Created At</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{shown.map((item: any, i: number) => {
								const index = (page - 1) * PAGE_SIZE + i;
								const providerKey = PROVIDER_LABEL[item.provider?.toLowerCase()] ?? 'esign';
								const type = item.type ?? 'docusign';
								return (
									<tr key={item._id} title={item.templateId ?? item.documentId}>
										<td className="od subcol">
											<EditTemplateName item={item} keyName="name" index={index} />
										</td>
										<td className="mid">{sizeOf(item)}</td>
										<td className="mid">{item.recipients?.length ?? 'NA'}</td>
										<td className="mid">
											<span className="vch cfg">
												{providerKey === 'esign' ? 'eSign' : 'Docusign'}
											</span>
										</td>
										<td className="mid">
											<span className="vch ns">{type}</span>
										</td>
										<td className="mid">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '--'}</td>
										<td className="accol">
											<div className="accol-actions">
											{/* USE. The action the deck existed to offer and did not
											    have: a saved template could be opened or deleted, but
											    never actually USED, so there was no way to send an
											    invite from one. This is the legacy row's own component
											    (`esign-use-template.tsx`) rather than a reimplementation
											    — it carries the subscription check, the invite-allowance
											    check, the vault token, and the basic/overlay choice
											    between the web-component and iframe embeds. Rebuilding
											    any of that here would be a second copy to keep in step. */}
											{isUserPermissionWrite && (
												<SimpliciUseTemplate template={item} />
											)}
											<Kebab
												items={[
													...(isUserPermissionWrite
														? [
																{
																	label: 'View / Edit template',
																	onClick: () => editTemplate(item),
																},
														  ]
														: []),
													{
														label: 'Open file',
														onClick: () => openTemplateFile(item),
													},
													...(isUserPermissionWrite
														? [{ label: 'Delete template', onClick: () => askDelete(item._id) }]
														: []),
												]}
											/>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				{isLoading && <div className="sv2-loading">Loading templates…</div>}
				{isLoaded && !isLoading && filtered.length === 0 && (
					<div className="sv2-empty">
						{signedDocs.length === 0
							? 'No templates yet — create one to get started.'
							: 'No templates match this search.'}
					</div>
				)}
			</div>

			<CreateTemplateSteps
				handleOnClickSaveCreateDoc={handleSaveCreateDoc}
				isDisabled={isCreateDisabled}
			/>

			{/* The esign editor, the step this deck was missing. Same
			    ReactPipelineModal + SimpliciSignIframe pairing the legacy page
			    uses, so overlay/esign/editable all land in the real configure
			    screen instead of a toast. */}
			<ReactPipelineModal
				handleClose={handleCloseConfig}
				body={
					<div className="iframe-container esign-templates-deck__iframe-wrap">
						{/* THE ONLY WAY OUT OF THIS SCREEN.
						    `showHeader={false}` below removes ReactPipelineModal's
						    own `Exit`, and the editor inside the iframe has no close
						    of its own — so opening a template was a one-way door:
						    nothing but a browser Back (which leaves the whole deck)
						    dismissed it. The legacy edit modal carried exactly this
						    button for the same reason (`edit-template.tsx`). */}
						<button
							type="button"
							className="esign-templates-deck__iframe-close"
							aria-label="Close editor"
							title="Close editor"
							onClick={handleCloseConfig}
						>
							<i className="ri-close-line" />
						</button>
						<SimpliciSignIframe
							signUrl={configDoc.prepareUrl}
							className="iframe mera-wala-iframe doc-sign-modal-wrapper"
							title="esign-template-configure"
							handleSubmitModal={handleConfiguredTemplate}
						/>
					</div>
				}
				visible={!!configDoc.prepareUrl}
				showFooter={false}
				showHeader={false}
				isFullScreen={false}
			/>

			<ConfirmationModal
				visible={confirmOpen}
				title="Delete template"
				description="This template will be permanently removed."
				boldDescription=""
				handleModal={confirmDelete}
				label="Delete"
			/>
		</div>
	);
};

export default EsignTemplatesDeck;
